import { ref, computed } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import {
  Country,
  SUPPORTED_COUNTRIES,
  COUNTRY_CONFIG,
} from "../constants/countries";
import { GooglePlace } from "../types";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useGoogleMapsIntegration() {
  const { t } = useI18n();
  const mapsApiKey = ref<string>(
    process.env.VUE_APP_GOOGLE_MAPS_AUTOCOMPLETE_KEY
  );
  const showAlert = ref<boolean>(false);
  const alertType = ref<string>("alert-danger");
  const alertMessage = ref<string>(
    t("PAYJOY_CARD_VIEWS.SHIPPING_FORM_COUNTRY_ERROR")
  );
  const alertClosable = ref<boolean>(true);
  const alertIcon = ref<string>("fa fa-exclamation-triangle");
  const userCountry = ref<Country | null>(null);

  const isColombiaUser = computed(() => userCountry.value === Country.COLOMBIA);
  const isMexicoUser = computed(() => userCountry.value === Country.MEXICO);
  const countryConfig = computed(() =>
    userCountry.value ? COUNTRY_CONFIG[userCountry.value] : null
  );

  const validateCountry = (place: GooglePlace): boolean => {
    const country = place.address_components.find((component) =>
      component.types.find((type) => type === "country")
    )?.short_name;

    if (country && SUPPORTED_COUNTRIES.includes(country as Country)) {
      userCountry.value = country as Country;
      return true;
    }

    userCountry.value = null;
    return false;
  };

  const extractAddressComponents = (place: GooglePlace) => {
    const components = {
      postalCode: "",
      street: "",
      streetNumber: "",
      region: "",
      neighborhood: "",
      city: "",
    };

    if (!place.formatted_address) return components;

    if (countryConfig.value?.requiresPostalCode) {
      components.postalCode =
        place.address_components.find((component) =>
          component.types.find((type) => type === "postal_code")
        )?.long_name || "";
    }

    components.street =
      place.address_components.find((component) =>
        component.types.find((type) => type === "route")
      )?.long_name || "";

    if (countryConfig.value?.requiresStreetNumber) {
      components.streetNumber =
        place.address_components.find((component) =>
          component.types.find((type) => type === "street_number")
        )?.long_name || "";
    }

    components.region =
      place.address_components.find((component) =>
        component.types.find((type) => type === "administrative_area_level_1")
      )?.long_name || "";

    components.neighborhood =
      place.address_components.find((component) =>
        component.types.find(
          (type) => type === "sublocality_level_1" || type === "neighborhood"
        )
      )?.long_name || "";

    components.city =
      place.address_components.find((component) =>
        component.types.find((type) => type === "locality")
      )?.long_name || "";

    return components;
  };

  const dismissAlert = () => {
    showAlert.value = false;
  };

  return {
    mapsApiKey,
    showAlert,
    alertType,
    alertMessage,
    alertClosable,
    alertIcon,
    validateCountry,
    extractAddressComponents,
    dismissAlert,
    isColombiaUser,
    isMexicoUser,
    userCountry,
    countryConfig,
  };
}
